import { Controller } from "react-hook-form";
import Button from "components/button";
import Icon from "components/icon";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useAddVehicleModelForm from "./useAddVehicleModelForm";

export const addVehicleModelPageRouteName =
  "/master-data/vehicle-model/add";

const AddVehicleModelPage = () => {
  const {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    t,
    isTruck,
    isFreezerTruck,
    vehicleTypeOptions,
    tollClassOptions,
    freezerModelOptions,
    fuelTypeOptions,
  } = useAddVehicleModelForm();

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-6">
        {/* Left Panel - Form */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("section-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {editingIndex !== null
                ? t("section-subtitle-edit")
                : t("section-subtitle-add")}
            </Typography>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAddToList();
            }}
            className="flex flex-col gap-4"
          >
            {/* Vehicle Category + Vehicle Type */}
            <div className="grid grid-cols-2 gap-4">
              <Controller
                name="vehicleCategory"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Typography variant="bodyLarge">
                      {t("label-vehicle-category")}{" "}
                      <span className="text-error-500">*</span>
                    </Typography>
                    <Select
                      options={[
                        { label: t("category-truck"), value: "truck" },
                        { label: t("category-chassis"), value: "chassis" },
                      ]}
                      value={field.value}
                      onValueChange={(val) => field.onChange(val)}
                      error={fieldState.error}
                      data-testid="dropdown-vehicle-category-model-add"
                    />
                  </div>
                )}
              />
              <Controller
                name="vehicleType"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Typography variant="bodyLarge">
                      {t("label-vehicle-type")}{" "}
                      <span className="text-error-500">*</span>
                    </Typography>
                    <Select
                      options={vehicleTypeOptions}
                      value={field.value}
                      onValueChange={(val) => field.onChange(val)}
                      error={fieldState.error}
                      placeholder={t("label-vehicle-type")}
                      data-testid="dropdown-vehicle-type"
                    />
                  </div>
                )}
              />
            </div>

            {/* Vehicle Model Name */}
            <Input
              label={t("label-vehicle-model")}
              placeholder={t("label-vehicle-model")}
              required
              {...register("vehicleModel")}
              error={errors.vehicleModel}
              data-testid="input-vehicle-model"
            />

            {/* Freezer Model - Conditional: truck + Freezer Truck */}
            {isFreezerTruck && (
              <Controller
                name="freezerModel"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Typography variant="bodyLarge">
                      {t("label-freezer-model")}{" "}
                      <span className="text-error-500">*</span>
                    </Typography>
                    <Select
                      options={freezerModelOptions}
                      value={field.value}
                      onValueChange={(val) => field.onChange(val)}
                      error={fieldState.error}
                      placeholder={t("label-freezer-model")}
                      data-testid="dropdown-freezer-model"
                    />
                  </div>
                )}
              />
            )}

            {/* Toll Class + Axle Count */}
            <div className="grid grid-cols-2 gap-4">
              <Controller
                name="tollClass"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Typography variant="bodyLarge">
                      {t("label-toll-class")}{" "}
                      <span className="text-error-500">*</span>
                    </Typography>
                    <Select
                      options={tollClassOptions}
                      value={field.value}
                      onValueChange={(val) => field.onChange(val)}
                      error={fieldState.error}
                      placeholder={t("label-toll-class")}
                      data-testid="dropdown-toll-class"
                    />
                  </div>
                )}
              />
              <Input
                label={t("label-axle-count")}
                placeholder={t("label-axle-count")}
                type="number"
                required
                {...register("axleCount")}
                error={errors.axleCount}
                data-testid="input-axle-count"
              />
            </div>

            {/* Tyre Count + Tyre Size */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t("label-tyre-count")}
                placeholder={t("label-tyre-count")}
                type="number"
                required
                {...register("tyreCount")}
                error={errors.tyreCount}
                data-testid="input-tyre-count"
              />
              <Controller
                name="tyreSize"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label={t("label-tyre-size")}
                    placeholder={t("label-tyre-size")}
                    type="text"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={fieldState.error}
                    data-testid="input-tyre-size"
                  />
                )}
              />
            </div>

            {/* Fuel Type + Fuel Capacity - Conditional: truck only */}
            {isTruck && (
              <div className="grid grid-cols-2 gap-4">
                <Controller
                  name="fuelType"
                  control={control}
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-1.5">
                      <Typography variant="bodyLarge">
                        {t("label-fuel-type")}{" "}
                        <span className="text-error-500">*</span>
                      </Typography>
                      <Select
                        options={fuelTypeOptions}
                        value={field.value}
                        onValueChange={(val) => field.onChange(val)}
                        error={fieldState.error}
                        placeholder={t("label-fuel-type")}
                        data-testid="dropdown-fuel-type"
                      />
                    </div>
                  )}
                />
                <Controller
                  name="fuelCapacity"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      label={t("label-fuel-capacity")}
                      placeholder={t("label-fuel-capacity")}
                      type="number"
                      enableNumberFormat
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={fieldState.error}
                      data-testid="input-fuel-capacity"
                    />
                  )}
                />
              </div>
            )}

            {/* Status */}
            <Controller
              name="status"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-status")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={[
                      { label: t("status-active"), value: "active" },
                      { label: t("status-inactive"), value: "inactive" },
                    ]}
                    value={field.value}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-model-status"
                  />
                </div>
              )}
            />

            {editingIndex !== null ? (
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  color="tertiary"
                  size="medium"
                  isFullSize
                  onClick={handleDiscardChanges}
                  data-testid="btn-discard-changes-model"
                >
                  {t("btn-discard-changes")}
                </Button>
                <Button
                  type="submit"
                  variant="fill"
                  color="primary"
                  size="medium"
                  isFullSize
                  data-testid="btn-save-changes-model"
                >
                  {t("btn-save-changes")}
                </Button>
              </div>
            ) : (
              <Button
                type="submit"
                variant="fill"
                color="primary"
                size="medium"
                isFullSize
                data-testid="btn-add-to-list-model"
              >
                {t("btn-add-to-list")}
              </Button>
            )}
          </form>
        </section>

        {/* Right Panel - Items List */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("list-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {t("list-subtitle")}
            </Typography>
          </div>

          {fields.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon
                name="document-text"
                size={48}
                className="text-greyScale-60 mb-4"
              />
              <Typography variant="body" tone="muted">
                {t("no-data-title")}
              </Typography>
              <Typography variant="body" tone="muted">
                {t("no-data-subtitle")}
              </Typography>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-[500px] overflow-y-auto">
              {fields.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center justify-between rounded-xl border p-4 ${
                    editingIndex === index
                      ? "border-primary-500 bg-primary-50"
                      : "border-border"
                  }`}
                >
                  <div className="flex flex-col gap-1">
                    <Typography variant="bodyLarge" className="font-semibold">
                      {item.vehicleModel}
                    </Typography>
                    <div className="flex items-center gap-2">
                      <Typography variant="body" tone="muted">
                        {t("label-vehicle-type")}
                      </Typography>
                      <Typography variant="body" tone="muted">
                        :
                      </Typography>
                      <Typography variant="body">
                        {item.vehicleType}
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <Typography variant="body" tone="muted">
                        {t("label-vehicle-category")}
                      </Typography>
                      <Typography variant="body" tone="muted">
                        :
                      </Typography>
                      <Typography
                        variant="body"
                        className="capitalize"
                      >
                        {item.vehicleCategory}
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <Typography variant="body" tone="muted">
                        {t("label-status")}
                      </Typography>
                      <Typography variant="body" tone="muted">
                        :
                      </Typography>
                      <Typography
                        variant="body"
                        className="capitalize"
                      >
                        {item.status}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleEditItem(index)}
                      className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer ${
                        editingIndex === index
                          ? "bg-primary-500 text-white"
                          : "hover:bg-greyScale-90 text-primary-500"
                      }`}
                      data-testid={`btn-edit-row-model-${index}`}
                    >
                      <Icon name="edit-2" size={16} />
                    </button>
                    {fields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleDeleteItem(index)}
                        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-error-50 text-error-500 transition-colors cursor-pointer"
                        data-testid={`btn-delete-row-model-${index}`}
                      >
                        <Icon name="trash" size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={handleCancel}
          data-testid="btn-cancel-model"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={() => setShowSaveModal(true)}
          disabled={fields.length === 0}
          data-testid="btn-save-model"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleSave}
        onCancel={() => setShowSaveModal(false)}
        testId="save-model-modal"
      />
    </div>
  );
};

export default AddVehicleModelPage;
